import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useMutation } from "@apollo/client";
import { REQUEST_MAGIC_LINK } from "./magiclink.gql";
import OtpForm from "./OtpForm";
import BrowserPersistence from "@overrides_venia/peregrine/util/simplePersistence";
import { useUserContext } from "@overrides_venia/peregrine/context/user";
import { useIntl, FormattedMessage } from "react-intl";
import Button from "@magento/venia-ui/lib/components/Button";
import { mergeClasses } from "@magento/venia-ui/lib/classify";
import defaultClasses from "./magicLinkLogin.module.scss";
import { SimpleField } from "@Components/FormFields/SimpleField";
import { isEmailValid as validateEmailFormat } from "@Src_main/utils/generalUtils";
import { useAwaitQuery } from "@magento/peregrine/lib/hooks/useAwaitQuery";
import { GET_CUSTOMER } from "@overrides_venia/peregrine/talons/SignIn/signIn.gql";
import UIkit from "uikit";
var MagicLinkLogin = _ref => {
  var {
    onSuccess,
    onError,
    classes: propsClasses,
    onStepChange,
    type = "LOGIN"
  } = _ref;
  var classes = mergeClasses(defaultClasses, propsClasses);
  var {
    formatMessage
  } = useIntl();
  var [email, setEmail] = useState("");
  var [emailError, setEmailError] = useState("");
  var [isEmailValidState, setIsEmailValidState] = useState(false);
  var [showOtpForm, setShowOtpForm] = useState(false);
  var [isSubmitting, setIsSubmitting] = useState(false);
  var [successMessage, setSuccessMessage] = useState("");
  var storage = new BrowserPersistence();
  var [{
    isSignedIn
  }, {
    getUserDetails
  }] = useUserContext();
  var hasHandledSuccessRef = useRef(false);
  var checkIntervalRef = useRef(null);
  var fetchUserDetails = useAwaitQuery(GET_CUSTOMER);
  var [requestMagicLink] = useMutation(REQUEST_MAGIC_LINK, {
    fetchPolicy: "no-cache"
  });

  // Listen for token changes in localStorage (for magic link)
  useEffect(() => {
    // Only start polling when OTP form is shown (user is expecting auth)
    if (!showOtpForm) {
      return;
    }
    checkIntervalRef.current = setInterval(() => {
      var token = storage.getItem("signin_token");
      if (token && !isSignedIn && !hasHandledSuccessRef.current) {
        // Token was set (likely from magic link page)
        hasHandledSuccessRef.current = true;
        handleLoginSuccess(token);
      }
    }, 1000);
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [isSignedIn, showOtpForm]);
  var handleLoginSuccess = useCallback( /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(function* (token) {
      try {
        // Stop the polling interval
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = null;
        }
        if (onSuccess) {
          onSuccess(token);
        }
      } catch (error) {
        console.error("Error fetching user details after login:", error);
        if (onError) {
          onError(error);
        }
      }
    });
    return function (_x) {
      return _ref2.apply(this, arguments);
    };
  }(), [getUserDetails, fetchUserDetails, onSuccess, onError]);
  var validateEmail = value => {
    var valid = validateEmailFormat(value);
    setIsEmailValidState(valid);
    if (!value) {
      setEmailError(formatMessage({
        id: "email.required"
      }));
      return false;
    } else if (!valid) {
      setEmailError(formatMessage({
        id: "email.invalid"
      }));
      return false;
    } else {
      setEmailError("");
      return true;
    }
  };
  var handleEmailChange = e => {
    var value = e.target.value;
    setEmail(value);
    if (value) {
      validateEmail(value);
    } else {
      setEmailError("");
      setIsEmailValidState(false);
    }
  };
  var handleSubmit = /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator(function* (e) {
      e.preventDefault();
      if (!validateEmail(email)) {
        return;
      }
      setIsSubmitting(true);
      setSuccessMessage("");
      try {
        var _response$data, _response$data$reques;
        var response = yield requestMagicLink({
          variables: {
            email,
            type
          }
        });
        if ((_response$data = response.data) !== null && _response$data !== void 0 && (_response$data$reques = _response$data.requestMagicLink) !== null && _response$data$reques !== void 0 && _response$data$reques.success) {
          // Reset the handled flag when showing OTP form
          hasHandledSuccessRef.current = false;
          setShowOtpForm(true);
          if (onStepChange) onStepChange('otp');
          setSuccessMessage(formatMessage({
            id: "magiclink.sent"
          }));
        } else {
          var _response$data2, _response$data2$reque;
          var message = (_response$data2 = response.data) === null || _response$data2 === void 0 ? void 0 : (_response$data2$reque = _response$data2.requestMagicLink) === null || _response$data2$reque === void 0 ? void 0 : _response$data2$reque.message;
          throw new Error(message || "Failed to send magic link");
        }
      } catch (err) {
        var errorMessage = err.message || formatMessage({
          id: "magiclink.error"
        });
        if (errorMessage === "Email not registered.") {
          // Dispatch event to prefill registration email
          var event = new CustomEvent('prefillRegistrationEmail', {
            detail: {
              email
            }
          });
          window.dispatchEvent(event);

          // Switch to registration offcanvas
          try {
            UIkit.offcanvas("#offcanvas-login-dx").hide();
            UIkit.offcanvas("#offcanvas-registration-dx").show();
          } catch (e) {
            console.error("Error switching offcanvas:", e);
          }
          return;
        }
        setEmailError(errorMessage);
        if (onError) {
          onError(err);
        }
      } finally {
        setIsSubmitting(false);
      }
    });
    return function handleSubmit(_x2) {
      return _ref3.apply(this, arguments);
    };
  }();
  var handleOtpSuccess = token => {
    hasHandledSuccessRef.current = true;
    handleLoginSuccess(token);
  };
  var handleOtpError = error => {
    if (onError) {
      onError(error);
    }
  };
  var handleBackToEmail = () => {
    setShowOtpForm(false);
    if (onStepChange) onStepChange('email');
    setSuccessMessage("");
    hasHandledSuccessRef.current = false;
    // Clear interval when going back
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }
  };
  if (showOtpForm) {
    return /*#__PURE__*/React.createElement("div", {
      className: classes.container
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.successMessage,
      style: {
        textAlign: 'center',
        marginBottom: '1rem'
      }
    }, /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "magiclink.sent.explicit",
      defaultMessage: "We\u2019ve sent a 6-digit code to {email}",
      values: {
        email: /*#__PURE__*/React.createElement("b", null, email)
      }
    })), /*#__PURE__*/React.createElement(OtpForm, {
      email: email,
      onSuccess: handleOtpSuccess,
      onError: handleOtpError,
      type: type
    }), /*#__PURE__*/React.createElement("div", {
      className: classes.divider
    }, /*#__PURE__*/React.createElement("span", null, formatMessage({
      id: "or"
    }))), /*#__PURE__*/React.createElement("button", {
      onClick: handleBackToEmail,
      className: classes.textButton,
      style: {
        background: 'none',
        border: 'none',
        color: '#666',
        textDecoration: 'underline',
        cursor: 'pointer',
        width: '100%',
        padding: '10px',
        marginTop: '10px'
      }
    }, formatMessage({
      id: "magiclink.back"
    })));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: classes.container
  }, /*#__PURE__*/React.createElement("form", {
    onSubmit: handleSubmit,
    className: classes.emailForm
  }, /*#__PURE__*/React.createElement("p", {
    className: classes.subtitle
  }, formatMessage({
    id: "magiclink.description"
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.inputWrapper
  }, /*#__PURE__*/React.createElement(SimpleField, {
    id: "email",
    type: "email",
    label: formatMessage({
      id: "email"
    }),
    value: email,
    onChange: handleEmailChange,
    placeholder: formatMessage({
      id: "email.placeholder"
    }),
    autoComplete: "email",
    disabled: isSubmitting,
    error: emailError
  })), emailError && /*#__PURE__*/React.createElement("div", {
    className: classes.errorMessage
  }, emailError), /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    disabled: isSubmitting || !isEmailValidState || !email,
    id: "magicLinkRequestButton",
    color: "primary",
    fullScreen: true
  }, isSubmitting ? formatMessage({
    id: "sending"
  }) : formatMessage({
    id: "magiclink.send"
  }))));
};
export default MagicLinkLogin;